import AppIcon from '../components/AppIcon';
import React, { useState } from 'react';

const MEMORY_TYPES = [
  { key: 'PHOTO', label: 'Photo', icon: 'photo' },
  { key: 'STORY', label: 'Story', icon: 'book' },
  { key: 'AUDIO', label: 'Audio', icon: 'audio' },
  { key: 'VIDEO', label: 'Video', icon: 'video' },
];

// Demo data
const DEMO_MEMORIES = [
  { id: '1', title: 'Family Reunion 2024', type: 'PHOTO', date: '2024-07-15', tags: ['reunion', 'summer'], member: 'Kao Saetern' },
  { id: '2', title: 'Grandpa\'s War Stories', type: 'STORY', date: '2023-12-25', tags: ['holidays', 'stories'], member: 'Grandpa Saetern' },
  { id: '3', title: 'Traditional Mien Song', type: 'AUDIO', date: '2024-03-10', tags: ['music', 'culture'], member: 'Grandma Saetern' },
  { id: '4', title: 'Wedding Dance', type: 'VIDEO', date: '2023-06-20', tags: ['wedding', 'dance'], member: 'Family' },
  { id: '5', title: 'New Year Celebration', type: 'PHOTO', date: '2024-02-10', tags: ['new-year', 'celebration'], member: 'Family' },
  { id: '6', title: 'Cooking Traditional Food', type: 'STORY', date: '2024-01-15', tags: ['cooking', 'food'], member: 'Auntie Mai' },
];

function MemoriesPage() {
  const [search, setSearch] = useState('');
  const [typeFilters, setTypeFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('grid');

  const toggleType = (type) => {
    setTypeFilters((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const filtered = DEMO_MEMORIES.filter((m) => {
    const matchesSearch = !search ||
      m.title.toLowerCase().includes(search.toLowerCase()) ||
      m.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()));
    const matchesType = typeFilters.length === 0 || typeFilters.includes(m.type);
    return matchesSearch && matchesType;
  });

  return (
    <div className="">

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Results Count */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-500">
            {filtered.length} memor{filtered.length === 1 ? 'y' : 'ies'}
            {search && ` matching "${search}"`}
            {typeFilters.length > 0 && ` · ${typeFilters.join(', ')}`}
          </p>
        </div>

        {filtered.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
            <AppIcon name="image" size={60} />
            <h3 className="mt-4 text-xl font-semibold text-gray-900">
              {search || typeFilters.length > 0 ? 'No memories found' : 'No memories yet'}
            </h3>
            <p className="mt-2 text-gray-500">
              {search || typeFilters.length > 0
                ? 'Try adjusting your search or filters.'
                : 'Start by adding your first family memory.'}
            </p>
            <a href="/memories/new" className="inline-block mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors">
              + Add Memory
            </a>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filtered.map((memory) => (
              <MemoryCard key={memory.id} memory={memory} />
            ))}
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((memory) => (
              <MemoryListItem key={memory.id} memory={memory} />
            ))}
          </div>
        )}

        {/* Pagination Placeholder */}
        {filtered.length > 0 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <button className="px-3 py-2 text-sm text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              Previous
            </button>
            <span className="px-3 py-2 text-sm text-gray-700 bg-indigo-600 text-white rounded-lg font-medium">1</span>
            <button className="px-3 py-2 text-sm text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              2
            </button>
            <button className="px-3 py-2 text-sm text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              3
            </button>
            <button className="px-3 py-2 text-sm text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
              Next
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

function MemoryCard({ memory }) {
  const typeConfig = MEMORY_TYPES.find((t) => t.key === memory.type);
  const typeColors = {
    PHOTO: 'bg-blue-50 text-blue-700 border-blue-200',
    STORY: 'bg-green-50 text-green-700 border-green-200',
    AUDIO: 'bg-purple-50 text-purple-700 border-purple-200',
    VIDEO: 'bg-red-50 text-red-700 border-red-200',
  };

  return (
    <a href={`/memories/${memory.id}`} className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-indigo-200 transition-all overflow-hidden group">
      <div className="aspect-video bg-gray-100 flex items-center justify-center relative">
        <AppIcon name={typeConfig?.icon} size={36} />
        <span className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-[10px] font-medium border ${typeColors[memory.type]}`}>
          {memory.type}
        </span>
      </div>
      <div className="p-4">
        <h3 className="text-sm font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors truncate">{memory.title}</h3>
        <p className="text-xs text-gray-500 mt-1">{memory.date} · {memory.member}</p>
        <div className="flex flex-wrap gap-1 mt-2">
          {memory.tags.map((tag) => (
            <span key={tag} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">#{tag}</span>
          ))}
        </div>
      </div>
    </a>
  );
}

function MemoryListItem({ memory }) {
  const typeConfig = MEMORY_TYPES.find((t) => t.key === memory.type);

  return (
    <a href={`/memories/${memory.id}`} className="flex items-center gap-4 bg-white rounded-xl shadow-sm border border-gray-100 p-4 hover:shadow-md hover:border-indigo-200 transition-all">
      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center shrink-0">
        <AppIcon name={typeConfig?.icon} size={24} />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm font-semibold text-gray-900 truncate">{memory.title}</h3>
        <p className="text-xs text-gray-500 mt-0.5">{memory.date} · {memory.member}</p>
        <div className="flex flex-wrap gap-1 mt-1">
          {memory.tags.map((tag) => (
            <span key={tag} className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px]">#{tag}</span>
          ))}
        </div>
      </div>
      <div className="shrink-0">
        <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
          memory.type === 'PHOTO' ? 'bg-blue-50 text-blue-700' :
          memory.type === 'STORY' ? 'bg-green-50 text-green-700' :
          memory.type === 'AUDIO' ? 'bg-purple-50 text-purple-700' :
          'bg-red-50 text-red-700'
        }`}>
          {memory.type}
        </span>
      </div>
    </a>
  );
}

export default MemoriesPage;